import React from "react";
import RuleBodyPage from "components/common/RuleBodyPage";
import { RuleModalBodyProps } from "components/common/RuleModal";
import { useTranslation } from "react-i18next";

/**
 * 딜러잭(블랙잭) 게임 규칙 설명
 */
const DealerJackRuleBody: React.FC<RuleModalBodyProps> = ({ page }) => {
  const { t } = useTranslation();
  const rules = t("Dealerjack.rules", { returnObjects: true }) as string[];

  const phaseContainer =
    "flex items-center gap-3 p-3 bg-gray-800 rounded-lg mb-4";
  const iconContainer =
    "w-8 h-8 min-w-8 min-h-8 flex items-center justify-center rounded-full text-sm";

  return (
    <div className="relative flex">
      {/* Page 1: 게임 개요 및 기본 규칙 */}
      <RuleBodyPage show={page === 0}>
        <div className="space-y-4">
          <div className="text-gray-100 mb-4">
            {rules[0] ||
              "🎯 목표: 딜러보다 21에 가까운 수를 만들되, 21을 넘으면 안 됩니다."}
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-yellow-600`}>
              <span className="text-sm">🃏</span>
            </div>
            <div className="flex-1">
              <div className="font-medium text-yellow-300 mb-3">
                {t("Dealerjack.cardValues") || "카드 값"}
              </div>

              <div className="text-sm text-gray-300 mb-3">
                {rules[1]?.replace("🃏 카드 값: ", "") ||
                  "A는 1 또는 11, J/Q/K는 10, 나머지는 숫자 그대로입니다."}
              </div>

              {/* 카드 예시들을 나란히 배치 */}
              <div className="space-y-4">
                {/* A 카드 */}
                <div className="flex items-center gap-4">
                  <div className="w-9 h-12 bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow-sm">
                    <span className="text-red-600 text-sm leading-none">♥</span>
                    <span className="text-red-600 font-bold text-lg">A</span>
                  </div>
                  <div className="text-gray-300 text-sm">
                    {"= 1 " +
                      t("Dealerjack.or") +
                      " 11 (" +
                      (t("Dealerjack.autoCalculated") || "자동 계산") +
                      ")"}
                  </div>
                </div>

                {/* J, Q, K 카드들 */}
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    <div className="w-9 h-12 bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow-sm">
                      <span className="text-black text-sm leading-none">♠</span>
                      <span className="text-black font-bold text-lg">J</span>
                    </div>
                    <div className="w-9 h-12 bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow-sm">
                      <span className="text-red-600 text-sm leading-none">
                        ♦
                      </span>
                      <span className="text-red-600 font-bold text-lg">Q</span>
                    </div>
                    <div className="w-9 h-12 bg-white rounded border border-gray-300 flex flex-col items-center justify-center shadow-sm">
                      <span className="text-black text-sm leading-none">♣</span>
                      <span className="text-black font-bold text-lg">K</span>
                    </div>
                  </div>
                  <div className="text-gray-300 text-sm">= 10</div>
                </div>
              </div>
            </div>
          </div>

          <div className={`${phaseContainer}`}>
            <div className={`${iconContainer} bg-yellow-600`}>
              <i className="fas fa-coins" />
            </div>
            <div>
              <div className="font-medium text-yellow-300 mb-1">
                {t("Dealerjack.bet") || "베팅"}
              </div>
              <div className="text-sm text-gray-300">
                {rules[2]?.replace("💰 베팅: ", "") ||
                  "각 라운드마다 칩을 걸고 시작합니다."}
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>

      {/* Page 2: 플레이어 액션 및 승패 */}
      <RuleBodyPage show={page === 1}>
        <div className="space-y-3">
          {/* 플레이어 액션 */}
          <div className={`${phaseContainer.replace("mb-4", "mb-2")}`}>
            <div className={`${iconContainer} bg-red-600`}>
              <i className="fas fa-plus" />
            </div>
            <div>
              <div className="font-medium text-red-300 mb-1">
                {t("Dealerjack.hit") || "히트"}
              </div>
              <div className="text-sm text-gray-300">
                {rules[3]?.replace("🎲 Hit: ", "") ||
                  t("Dealerjack.hitDescription") ||
                  "카드를 더 받고 싶을 때 선택합니다."}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer.replace("mb-4", "mb-2")}`}>
            <div className={`${iconContainer} bg-green-600`}>
              <i className="fas fa-hand-paper" />
            </div>
            <div>
              <div className="font-medium text-green-300 mb-1">
                {t("Dealerjack.stand") || "스탠드"}
              </div>
              <div className="text-sm text-gray-300">
                {rules[4]?.replace("✋ Stand: ", "") ||
                  t("Dealerjack.standDescription") ||
                  "더 이상 카드를 받지 않을 때 선택합니다."}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer.replace("mb-4", "mb-2")}`}>
            <div className={`${iconContainer} bg-purple-600`}>
              <i className="fas fa-angle-double-up" />
            </div>
            <div>
              <div className="font-medium text-purple-300 mb-1">
                {t("Dealerjack.doubleDown") || "더블다운"}
              </div>
              <div className="text-sm text-gray-300">
                {t("Dealerjack.doubleDownDescription") ||
                  "첫 번째 액션으로만 가능. 베팅을 2배로 늘리고 카드 1장만 받은 후 자동으로 스탠드합니다."}
              </div>
            </div>
          </div>

          <div className={`${phaseContainer.replace("mb-4", "mb-2")}`}>
            <div className={`${iconContainer} bg-orange-600`}>
              <i className="fas fa-flag" />
            </div>
            <div>
              <div className="font-medium text-orange-300 mb-1">
                {t("Dealerjack.surrender") || "서렌더"}
              </div>
              <div className="text-sm text-gray-300">
                {t("Dealerjack.surrenderDescription") ||
                  "첫 번째 액션으로만 가능. 라운드를 포기하고 베팅액의 50%를 돌려받습니다."}
              </div>
            </div>
          </div>
          {/* Blackjack and Bust explanations */}
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <span className="text-yellow-400 font-bold">🌟</span>
                <span>
                  {rules[5] ||
                    "🌟 " +
                      (t("Dealerjack.blackjack") || "블랙잭") +
                      ": " +
                      (t("Dealerjack.blackjackDescription") ||
                        "처음 2장으로 21을 만들면 1.5배 배당을 받습니다.")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-400 font-bold">💥</span>
                <span>
                  {rules[6] ||
                    "💥 " +
                      (t("Dealerjack.bust") || "버스트") +
                      ": " +
                      (t("Dealerjack.bustDescription") ||
                        "21을 넘으면 자동으로 패배합니다.")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </RuleBodyPage>
      <RuleBodyPage show={page === 2}>
        {/* 승패 결정 */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="font-medium text-indigo-300 mb-2">
            🏆 {t("Dealerjack.winConditions") || "승패 결정"}
          </div>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-300">
            <li>
              🌟 {t("Dealerjack.blackjack") || "블랙잭"} (A+10):{" "}
              {t("Dealerjack.blackjackPayout") || "베팅액의 1.5배 지급"}
            </li>
            <li>
              ✨ {t("Dealerjack.normalWin") || "일반 승리"}:{" "}
              {t("Dealerjack.normalWinPayout") || "베팅액의 2배 지급"}
            </li>
            <li>
              🤝 {t("Dealerjack.push") || "무승부"}:{" "}
              {t("Dealerjack.pushPayout") || "베팅액 그대로 반환"}
            </li>
            <li>
              💔 {t("Dealerjack.lose") || "패배"}:{" "}
              {t("Dealerjack.loseDescription") || "베팅액 잃음"}
            </li>
          </ul>
        </div>

        {/* 딜러 규칙 */}
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="font-medium text-indigo-300 mb-2 flex items-center gap-2">
            <i className="fas fa-user-tie text-yellow-400"></i>
            {t("Dealerjack.dealerRules") || "딜러 규칙"}
          </div>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-300">
            <li>
              {t("Dealerjack.dealerRule1") ||
                "딜러는 핸드 값이 16 이하일 때 반드시 카드를 추가로 받아야 합니다"}
            </li>
            <li>
              {t("Dealerjack.dealerRule2") ||
                "딜러는 핸드 값이 17 이상일 때 반드시 스탠드해야 합니다"}
            </li>
            <li>
              {t("Dealerjack.dealerRule3") ||
                "딜러의 두 번째 카드는 모든 플레이어의 턴이 끝날 때까지 숨겨집니다"}
            </li>
            <li>
              {t("Dealerjack.dealerRule4") ||
                "딜러는 소프트 17(A가 포함된 17)에서도 스탠드합니다"}
            </li>
          </ul>
        </div>

        {/* 전략 팁 */}
        {/* <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="font-medium text-indigo-300 mb-2">💡 전략 팁</div>
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-300">
            <li>딜러의 오픈 카드가 2-6일 때는 보수적으로 플레이하세요</li>
            <li>딜러의 오픈 카드가 7-A일 때는 적극적으로 카드를 받으세요</li>
            <li>핸드 값이 11 이하일 때는 항상 히트하세요</li>
            <li>더블다운은 핸드 값이 9-11일 때 고려하세요</li>
            <li>서렌더는 핸드가 매우 불리할 때만 사용하세요</li>
          </ul>
        </div> */}
      </RuleBodyPage>
    </div>
  );
};

export default DealerJackRuleBody;
